import { getResultFile } from '../resultFile';
import ModuleImage from '@/assets/image/module-default.png';
import { Camera } from '@/assets/svg';
import AsyncAutoComplete from '@/components/AsyncAutoComplete';
import {
  HS_RESULTS_REVIEW_DISPLAY_STATUS,
  HS_RESULTS_REVIEW_STATUS_COLORS,
} from '@/module/results-review-management/constants';
import { IOSSwitch } from '@/module/setup/components/mainScreen.styled';
import { CustomStatusComponent } from '@/new-components/CustomStatusComponent';
import {
  Box,
  InputLabel,
  makeStyles,
  withStyles,
  TextField,
  Paper,
} from '@material-ui/core';
import { Edit, CheckCircle } from '@material-ui/icons';
import { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';

const useStyle = () =>
  makeStyles({
    wrapperUpload: {
      borderRadius: 8,
      border: '1px solid #E0E0E0',
      backgroundColor: '#F5F6F9',
      marginBottom: 16,
    },

    editIcon: {
      position: 'absolute',
      bottom: 0,
      right: -10,
      textAlign: 'center',

      width: 20,
      height: 20,
      color: '#fff',
      backgroundColor: '#AD5E99',
      borderRadius: '50%',

      '& .icon': {
        fontSize: 14,
        verticalAlign: 'middle',
        marginBottom: 2,
      },
    },
  });

const CustomInputLabel = withStyles({
  root: {
    marginBottom: 8,
    fontWeight: 600,
    color: '#666666',
  },
})(InputLabel);

const CustomTextField = withStyles({
  root: {
    marginBottom: 24,
    '& .MuiOutlinedInput-root': {
      height: 48,
      borderRadius: 8,
    },
    '& .MuiInputBase-multiline': {
      height: 'auto',
      paddingLeft: 0,

      '& .MuiOutlinedInput-input': {
        marginRight: 8,
      },
    },

    '& .MuiOutlinedInput-input': {
      marginRight: 0,
      padding: '8px 5px',
      fontSize: 16,
    },
    '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]': {
      padding: 0,
    },
  },
})(TextField);

const CustomPaper = withStyles((theme) => ({
  root: {
    '& .MuiIconButton-root:hover': {
      backgroundColor: 'transparent',
    },

    '& .MuiAutocomplete-option': {
      padding: 8,
      borderRadius: 8,
      margin: '2px 8px',
      '&:first-child': {
        marginTop: 8,
      },
      '&:last-child': {
        marginBottom: 8,
      },
      '&.MuiAutocomplete-option[aria-selected="true"]': {
        color: theme.palette.common.white,
        backgroundColor: theme.palette.primary.main,
      },
    },
  },
}))(Paper);

const FormUpload = ({
  fileData,
  onChange,
  label,
  dataModule,
  listFiles,
  isDoctor,
  isDisabledByRadiologies,
  defaultDoctor,
  setDefaultDoctor,
  moduleDoctors,
  setModuleDoctors,
  note,
  setNote,
  byPassTests,
  setByPassTests,
}) => {
  const classes = useStyle(fileData)();
  const [localData, setLocalData] = useState([]);

  useEffect(() => {
    const newLocalData = dataModule.map((it1) => ({
      ...it1,
      ...listFiles.find((it2) => it2.moduleId === it1.moduleId),
    }));

    setLocalData(newLocalData);
  }, [dataModule, listFiles]);

  const renderForm = (data) => {
    if (isEmpty(data)) return null;
    const { moduleId, moduleName } = data;
    // Either a locally picked File, or the stored file URL from the API.
    const resultFile = getResultFile(data);
    let justifyContent = 'flex-start';

    let component = (
      <Box
        display="flex"
        alignItems="center"
        width="100%"
        justifyContent="center"
        fontSize={16}
      >
        <Camera color="primary" style={{ marginRight: 5 }} />
        {label}
      </Box>
    );

    if (resultFile) {
      justifyContent = 'space-between';
      component = (
        <>
          <Box
            position="relative"
            display="flex"
            justifyContent="space-between"
          >
            <div className={classes.editIcon}>
              <Edit className="icon" />
            </div>
            <img
              id={moduleId}
              src={ModuleImage}
              width="60"
              height="60"
              alt={moduleName}
            />
          </Box>
          <CheckCircle style={{ color: '#0B8E5E' }} />
        </>
      );
    }

    return (
      <Box
        display="flex"
        color="primary.main"
        alignItems="center"
        height={93}
        fontWeight="600"
        justifyContent={justifyContent}
      >
        {component}
      </Box>
    );
  };

  const onInputChange = (event, module) => {
    const file = event.target.files[0];
    if (file) {
      onChange('file', {
        ...module,
        resultFilePath: file,
      });
      setByPassTests((prev) =>
        prev.map((t) =>
          t.moduleId === module.moduleId ? { ...t, isReviewRequired: true } : t
        )
      );
    }
    event.target.value = '';
  };
  const hasDefaultDoctor = Boolean(defaultDoctor?.id);
  const hasModuleDoctors = Object.values(moduleDoctors).some(
    (doc) => doc && doc.id
  );
  const isDefaultDoctorDisabled =
    isDoctor || isDisabledByRadiologies || hasModuleDoctors;

  return (
    !isEmpty(localData) && (
      <>
        <CustomInputLabel>Assign Doctor</CustomInputLabel>

        <AsyncAutoComplete
          endpoint="fut-backrooms/valid-doctors"
          value={defaultDoctor}
          setValue={(newValue) => {
            setDefaultDoctor(newValue || {});
            if (newValue && newValue.id) {
              setModuleDoctors({});
            }
          }}
          getOptionLabel={(option) => option.fullName && `${option.fullName}`}
          renderInput={(params) => (
            <CustomTextField
              disabled={isDefaultDoctorDisabled}
              variant="outlined"
              {...params}
              placeholder="Select or search doctor"
            />
          )}
          isFormatResult
          nameKey="fullName"
          renderOption={(option) => option.fullName && `${option?.fullName}`}
          PaperComponent={({ children, ...other }) => (
            <CustomPaper {...other}>{children}</CustomPaper>
          )}
          disabled={isDefaultDoctorDisabled}
          disableClearable={false}
        />

        <CustomInputLabel>Note</CustomInputLabel>
        <CustomTextField
          rows={3}
          multiline
          fullWidth
          variant="outlined"
          placeholder="Enter note"
          disabled={isDisabledByRadiologies}
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />

        {localData.map((item) => {
          const isByPass = byPassTests.find(
            (t) => t.moduleId === item.moduleId
          )?.isReviewRequired;
          const isDisabledUpload =
            Boolean(item.isOptOut) || isDisabledByRadiologies;
          const isModuleDoctorDisabled =
            isDoctor || isDisabledByRadiologies || hasDefaultDoctor;

          return (
            <Box key={item.moduleId} className="module-upload-item">
              <Box
                pb={1}
                fontSize={14}
                fontWeight="600"
                fontFamily="OpenSans"
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box component="span" mr={1}>
                  {item.moduleName}
                </Box>

                <Box display="flex" alignItems="center" gridGap={8}>
                  {item.reviewStatus !== 'Unavailable' && (
                    <CustomStatusComponent
                      filterList={HS_RESULTS_REVIEW_DISPLAY_STATUS}
                      filterListColor={HS_RESULTS_REVIEW_STATUS_COLORS}
                      status={item.reviewStatus}
                    />
                  )}
                  <IOSSwitch
                    checked={isByPass || false}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setByPassTests((prev) =>
                        prev.map((t) =>
                          t.moduleId === item.moduleId
                            ? { ...t, isReviewRequired: checked }
                            : t
                        )
                      );
                    }}
                    disabled={isDisabledByRadiologies}
                  />
                </Box>
              </Box>

              <Box pb={1}>
                <AsyncAutoComplete
                  endpoint="fut-backrooms/valid-doctors"
                  value={
                    moduleDoctors[item.moduleId] ||
                    (hasDefaultDoctor ? defaultDoctor : {})
                  }
                  setValue={(newValue) => {
                    if (newValue && newValue.id) {
                      setDefaultDoctor({});
                    }
                    setModuleDoctors((prev) => {
                      const updated = { ...prev };
                      if (newValue && newValue.id) {
                        updated[item.moduleId] = newValue;
                      } else {
                        delete updated[item.moduleId];
                      }

                      return updated;
                    });
                  }}
                  getOptionLabel={(option) =>
                    option.fullName ? `${option.fullName}` : ''
                  }
                  renderInput={(params) => (
                    <CustomTextField
                      disabled={isModuleDoctorDisabled}
                      variant="outlined"
                      {...params}
                      placeholder="Select or search doctor"
                    />
                  )}
                  isFormatResult
                  nameKey="fullName"
                  renderOption={(option) =>
                    option.fullName ? `${option.fullName}` : ''
                  }
                  PaperComponent={({ children, ...other }) => (
                    <CustomPaper {...other}>{children}</CustomPaper>
                  )}
                  disabled={isModuleDoctorDisabled}
                  disableClearable={false}
                />
              </Box>

              <label htmlFor={isDisabledUpload ? undefined : item.moduleId}>
                <Box
                  px={2}
                  className={classes.wrapperUpload}
                  style={{
                    cursor: isDisabledUpload ? 'not-allowed' : 'pointer',
                    opacity: isDisabledUpload ? 0.6 : 1,
                  }}
                >
                  <input
                    id={item.moduleId}
                    accept=".pdf"
                    type="file"
                    hidden
                    disabled={isDisabledUpload}
                    onChange={(e) => onInputChange(e, item)}
                  />

                  {renderForm(item)}
                </Box>
              </label>
            </Box>
          );
        })}
      </>
    )
  );
};

export default FormUpload;
